import { arsenalScore } from './score';
import { PARTICIPATION_KEY } from './participation';
import { captureLocalCommunitySnapshot, transmitIfLocalCommunityGeneration } from './outbox';
import type { Fixture, FixtureSource, PredictionPick } from './types';

export const GAME_PASSPORT_KEY = 'coyg_game_passport_v1';
export const GAME_PASSPORT_EVENT = 'coyg:game-passport-changed';
export const GAME_TICKET_LIMIT = 200;
const DISCOVERY_LIMIT = 64;

export interface GameTicket {
  fixture: Fixture;
  season: string;
  checkedAt: string | null;
  sourceUrl: string | null;
  sourceProvider: string | null;
  licenseUrl: string | null;
  licenseLabel: string | null;
  call: PredictionPick | null;
  joined: boolean;
  saved: boolean;
  outcome: PredictionPick | null;
  score: { ours: number; theirs: number } | null;
}
export interface GamePassport {
  version: 1;
  tickets: GameTicket[];
  discoveries: string[];
  /** Explicit null remembers a withdrawal rather than importing an old call. */
  calls: Record<string, PredictionPick | null>;
  callVersions: Record<string, string>;
  bookCheckedAt?: string;
}
const empty = (): GamePassport => ({ version: 1, tickets: [], discoveries: [], calls: {}, callVersions: {} });
const text = (value: unknown, max = 160): value is string => typeof value === 'string' && value.length > 0 && value.length <= max;
const pick = (value: unknown): PredictionPick | null => value === 'W' || value === 'D' || value === 'L' ? value : null;
const safeUrl = (value: unknown): string | null => {
  if (!text(value, 1000)) return null;
  try { const url = new URL(value); return url.protocol === 'https:' ? url.href : null; } catch { return null; }
};
const date = (value: unknown): string | null => text(value, 40) && Number.isFinite(Date.parse(value)) ? value : null;
const validScore = (a: unknown, b: unknown): boolean => [a, b].every((value) => typeof value === 'number' && Number.isInteger(value) && value >= 0 && value <= 100);

/** Retain only public fixture fields; never persist arbitrary feed payloads. */
function cleanFixture(value: unknown): Fixture | null {
  if (!value || typeof value !== 'object') return null;
  const f = value as Fixture;
  if (!text(f.id) || !text(f.comp) || !text(f.opponent) || !text(f.opponentShort, 40)
    || !['H', 'A', 'N'].includes(f.venue) || !['scheduled', 'live', 'ft', 'postponed'].includes(f.status)) return null;
  return {
    id: f.id, kickoffUtc: date(f.kickoffUtc),
    ...(text(f.kickoffDate, 10) && /^\d{4}-\d{2}-\d{2}$/.test(f.kickoffDate) ? { kickoffDate: f.kickoffDate } : {}),
    comp: f.comp, opponent: f.opponent, opponentShort: f.opponentShort, venue: f.venue,
    ...(f.arsenalSide === 'home' || f.arsenalSide === 'away' ? { arsenalSide: f.arsenalSide } : {}),
    ...(text(f.ground) ? { ground: f.ground } : {}),
    ...(f.timeTbc ? { timeTbc: true } : {}),
    status: f.status,
    ...(f.statusQuality === 'overdue' ? { statusQuality: 'overdue' as const } : {}),
    score: f.score && validScore(f.score.home, f.score.away) ? { home: f.score.home, away: f.score.away } : null,
  };
}

function bounded(state: GamePassport): GamePassport {
  const unique = [...new Map(state.tickets.map((ticket) => [ticket.fixture.id, ticket])).values()];
  unique.sort((a, b) => Number(b.joined || b.saved || Boolean(b.call)) - Number(a.joined || a.saved || Boolean(a.call))
    || (Date.parse(b.fixture.kickoffUtc ?? b.fixture.kickoffDate ?? '') || 0) - (Date.parse(a.fixture.kickoffUtc ?? a.fixture.kickoffDate ?? '') || 0)
    || a.fixture.id.localeCompare(b.fixture.id));
  // Canonical property order prevents two mounted wallet views from repeatedly
  // rewriting equivalent snapshots with different JSON field order.
  const tickets = unique.slice(0, GAME_TICKET_LIMIT).map((ticket) => ({
    fixture: ticket.fixture, season: ticket.season, checkedAt: ticket.checkedAt,
    sourceUrl: ticket.sourceUrl, call: ticket.call, joined: ticket.joined,
    sourceProvider: ticket.sourceProvider, licenseUrl: ticket.licenseUrl, licenseLabel: ticket.licenseLabel,
    saved: ticket.saved, outcome: ticket.outcome, score: ticket.score,
  }));
  const entries = Object.entries(state.calls).filter(([id, value]) => text(id) && (value === null || pick(value))).slice(-GAME_TICKET_LIMIT);
  const callVersions = Object.fromEntries(entries.filter(([id]) => text(state.callVersions?.[id])).map(([id]) => [id, state.callVersions[id]]));
  return { version: 1, tickets, discoveries: [...new Set(state.discoveries.filter((id) => text(id, 100)))].slice(-DISCOVERY_LIMIT), calls: Object.fromEntries(entries), callVersions,
    ...(date(state.bookCheckedAt) ? { bookCheckedAt: state.bookCheckedAt } : {}) };
}

export function readGamePassport(): GamePassport {
  try {
    const raw = localStorage.getItem(GAME_PASSPORT_KEY);
    if (!raw || raw.length > 400_000) return empty();
    const parsed = JSON.parse(raw) as GamePassport;
    if (parsed.version !== 1 || !Array.isArray(parsed.tickets) || !Array.isArray(parsed.discoveries)) return empty();
    const tickets: GameTicket[] = [];
    for (const item of parsed.tickets.slice(0, GAME_TICKET_LIMIT * 2)) {
      if (!item || typeof item !== 'object') continue;
      const fixture = cleanFixture(item.fixture);
      if (!fixture || !text(item.season, 30)) continue;
      const score = item.score && validScore(item.score.ours, item.score.theirs) ? { ours: item.score.ours, theirs: item.score.theirs } : null;
      const expected = score ? (score.ours > score.theirs ? 'W' : score.ours < score.theirs ? 'L' : 'D') : null;
      tickets.push({ fixture, season: item.season, checkedAt: date(item.checkedAt), sourceUrl: safeUrl(item.sourceUrl),
        sourceProvider: text(item.sourceProvider) ? item.sourceProvider : null,
        licenseUrl: safeUrl(item.licenseUrl), licenseLabel: text(item.licenseLabel) ? item.licenseLabel : null,
        call: pick(item.call), joined: item.joined === true, saved: item.saved === true,
        outcome: fixture.status === 'ft' && pick(item.outcome) === expected ? expected : null,
        score: fixture.status === 'ft' && pick(item.outcome) === expected ? score : null });
    }
    const calls = parsed.calls && typeof parsed.calls === 'object' && !Array.isArray(parsed.calls) ? parsed.calls : {};
    const callVersions = parsed.callVersions && typeof parsed.callVersions === 'object' && !Array.isArray(parsed.callVersions) ? parsed.callVersions : {};
    return bounded({ version: 1, tickets, discoveries: parsed.discoveries, calls, callVersions, bookCheckedAt: parsed.bookCheckedAt });
  } catch { return empty(); }
}

function participated(): Set<string> {
  try {
    const raw = JSON.parse(localStorage.getItem(PARTICIPATION_KEY) ?? '[]') as unknown;
    return new Set(Array.isArray(raw) ? raw.filter((id): id is string => text(id)).slice(-GAME_TICKET_LIMIT) : []);
  } catch { return new Set(); }
}
function legacyCall(id: string): PredictionPick | null {
  try { return pick(JSON.parse(localStorage.getItem(`coyg_predict_cache:${id}`) ?? '{}').mine); } catch { return null; }
}

/** Pure reconciliation: every published match is a ticket; no timer, identity or network. */
export function reconcileGamePassport(state: GamePassport, fixtures: Fixture[], season: string, source?: FixtureSource, stale = false, complete = false): GamePassport {
  const tickets = new Map(state.tickets.map((ticket) => [ticket.fixture.id, ticket]));
  const currentIds = new Set<string>();
  const own = participated();
  const bookCheckedAt = date(source?.checkedAt ?? source?.syncedAt);
  if (complete && state.bookCheckedAt && (stale || source?.status === 'degraded' || !bookCheckedAt || Date.parse(bookCheckedAt) < Date.parse(state.bookCheckedAt))) {
    return bounded({ ...state, tickets: state.tickets.map((ticket) => ({ ...ticket,
      joined: ticket.joined || own.has(ticket.fixture.id),
      call: Object.hasOwn(state.calls, ticket.fixture.id) ? state.calls[ticket.fixture.id] : ticket.call })) });
  }
  for (const input of fixtures) {
    const fixture = cleanFixture(input);
    if (!fixture || !text(season, 30)) continue;
    currentIds.add(fixture.id);
    const prior = tickets.get(fixture.id);
    const call = Object.hasOwn(state.calls, fixture.id) ? state.calls[fixture.id] : prior?.call ?? legacyCall(fixture.id);
    const incomingChecked = date(source?.checkedAt ?? source?.syncedAt);
    const canSettle = !stale && source?.status !== 'degraded'
      && (!prior?.checkedAt || (incomingChecked !== null && Date.parse(incomingChecked) >= Date.parse(prior.checkedAt)))
      && (!prior?.sourceUrl || Boolean(source?.url));
    let score = prior?.score ?? null;
    let outcome = prior?.outcome ?? null;
    if (canSettle) {
      score = fixture.status === 'ft' && fixture.score && (fixture.venue !== 'N' || fixture.arsenalSide)
        ? arsenalScore(fixture.score, fixture.venue, fixture.arsenalSide) : null;
      outcome = score ? (score.ours > score.theirs ? 'W' : score.ours < score.theirs ? 'L' : 'D') : null;
    }
    // A stale regression cannot erase a previously admitted final record.
    const snapshot = !canSettle && prior ? prior.fixture : fixture;
    tickets.set(fixture.id, { fixture: snapshot, season: !canSettle && prior ? prior.season : season,
      checkedAt: canSettle ? incomingChecked : prior?.checkedAt ?? null,
      sourceUrl: canSettle ? safeUrl(source?.url) : prior?.sourceUrl ?? null,
      sourceProvider: canSettle ? source?.provider ?? null : prior?.sourceProvider ?? null,
      licenseUrl: canSettle ? safeUrl(source?.licenseUrl) : prior?.licenseUrl ?? null,
      licenseLabel: canSettle ? source?.licenseLabel ?? null : prior?.licenseLabel ?? null,
      call: pick(call), joined: own.has(fixture.id) || prior?.joined === true, saved: prior?.saved === true, score, outcome });
  }
  const current = [...tickets.values()].filter((ticket) => currentIds.has(ticket.fixture.id));
  const retained = bounded({ ...state, tickets: [...tickets.values()].filter((ticket) => !currentIds.has(ticket.fixture.id)
    && (!complete || stale || source?.status === 'degraded' || ticket.saved || ticket.joined || Boolean(ticket.call))) }).tickets;
  return bounded({ ...state, tickets: [...current, ...retained.slice(0, Math.max(0, GAME_TICKET_LIMIT - current.length))],
    ...(complete && !stale && source?.status !== 'degraded' && bookCheckedAt ? { bookCheckedAt } : {}) });
}

export function writeGamePassport(state: GamePassport): boolean {
  try {
    const encoded = JSON.stringify(bounded(state));
    if (localStorage.getItem(GAME_PASSPORT_KEY) === encoded) return true;
    localStorage.setItem(GAME_PASSPORT_KEY, encoded);
    if (localStorage.getItem(GAME_PASSPORT_KEY) !== encoded) return false;
    window.dispatchEvent(new Event(GAME_PASSPORT_EVENT));
    return true;
  } catch { return false; }
}

/** Only call after the existing prediction endpoint acknowledges this device's write. */
let gameMutationLock: Promise<void> = Promise.resolve();
async function withGameLock<T>(task: () => T | Promise<T>): Promise<T> {
  if (typeof navigator !== 'undefined' && navigator.locks) return navigator.locks.request('coyg-game-passport', { mode: 'exclusive' }, task);
  const previous = gameMutationLock;
  let release!: () => void;
  gameMutationLock = new Promise<void>((resolve) => { release = resolve; });
  await previous;
  try { return await task(); } finally { release(); }
}

/** Read inside the cross-tab lock and share the same erasure boundary as writes. */
export async function mutateGamePassport(update: (state: GamePassport) => GamePassport, expectedGeneration?: string, onCommit?: () => void): Promise<boolean> {
  // A late acknowledgement already has its generation. Do not mint a new
  // storage-generation key just to reject it after erasure.
  let generation = expectedGeneration;
  if (!generation) {
    const snapshot = await captureLocalCommunitySnapshot(() => true);
    if (!snapshot.value) return false;
    generation = snapshot.generation;
  }
  const committed = await transmitIfLocalCommunityGeneration(generation,
    () => withGameLock(() => {
      const saved = writeGamePassport(update(readGamePassport()));
      if (saved) onCommit?.();
      return saved;
    }), 'shared');
  return committed.transmitted && committed.value;
}

export async function recordConfirmedPrediction(matchId: string, call: PredictionPick | null, expectedGeneration?: string, accept: () => boolean = () => true, version?: string, onCommit?: () => void): Promise<boolean> {
  if (!text(matchId)) return false;
  let accepted = false;
  const committed = await mutateGamePassport((state) => (accepted = accept()) ? ({ ...state, calls: { ...state.calls, [matchId]: pick(call) },
    callVersions: version ? { ...state.callVersions, [matchId]: version } : state.callVersions,
    tickets: state.tickets.map((ticket) => ticket.fixture.id === matchId ? { ...ticket, call: pick(call) } : ticket) }) : state, expectedGeneration, () => { if (accepted) onCommit?.(); });
  return committed && accepted;
}
